// Guarda o catálogo dentro de /trails para respeitar as regras RTDB atuais:
// "trails": { ".read": "auth != null", ".write": "admin" }
export const PUBLIC_TRAILS_CATALOG_PATH = "trails/publicCatalog";

export function getPublicTrailRuntimeId(trail, fallbackUrl = "") {
  const source = trail && typeof trail === "object" ? trail : {};
  const identity = String(
    source.id ||
      source.filename ||
      source.cloud?.file ||
      source.file ||
      source.url ||
      source.gpxUrl ||
      source.downloadUrl ||
      fallbackUrl ||
      ""
  ).trim();
  return identity ? `public-${identity.replace(/^public-/, "")}` : "";
}

export function buildPublicTrailItemKey(filename) {
  const raw = String(filename || "").trim();
  if (!raw) return "";
  let out = "";
  for (const ch of raw) {
    if (/^[A-Za-z0-9_-]$/.test(ch)) {
      out += ch;
      continue;
    }
    const hex = ch.codePointAt(0).toString(16).toUpperCase();
    out += `_x${hex}_`;
  }
  return out;
}

function toFiniteNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function sortByOrderThenName(a, b) {
  const ao = toFiniteNumber(a?.order, Number.MAX_SAFE_INTEGER);
  const bo = toFiniteNumber(b?.order, Number.MAX_SAFE_INTEGER);
  if (ao !== bo) return ao - bo;
  return String(a?.name || a?.id || "").localeCompare(String(b?.name || b?.id || ""), "pt");
}

function sortTrailItems(a, b) {
  const regionComparison = String(a?.region || "").localeCompare(
    String(b?.region || ""),
    "pt",
    { numeric: true, sensitivity: "base" }
  );
  if (regionComparison !== 0) return regionComparison;
  return String(a?.name || a?.filename || "").localeCompare(
    String(b?.name || b?.filename || ""),
    "pt",
    { numeric: true, sensitivity: "base" }
  );
}

export function normalizePublicTrailsCatalog(raw) {
  const categoriesRaw =
    raw?.categories && typeof raw.categories === "object" ? raw.categories : {};
  const itemsRaw = raw?.items && typeof raw.items === "object" ? raw.items : {};
  const regionsRaw = raw?.regions && typeof raw.regions === "object" ? raw.regions : {};

  const categories = Object.entries(categoriesRaw)
    .map(([id, value]) => {
      const item = value && typeof value === "object" ? value : {};
      const name = String(item.name || "").trim();
      if (!id || !name) return null;
      return {
        id,
        name,
        order: toFiniteNumber(item.order, Number.MAX_SAFE_INTEGER),
        createdAt: toFiniteNumber(item.createdAt, 0),
        updatedAt: toFiniteNumber(item.updatedAt, 0),
      };
    })
    .filter(Boolean)
    .sort(sortByOrderThenName);

  const items = Object.entries(itemsRaw)
    .map(([key, value]) => {
      const item = value && typeof value === "object" ? value : {};
      const filename = String(item.filename || "").trim();
      if (!filename) return null;
      return {
        key,
        filename,
        categoryId: String(item.categoryId || "").trim() || null,
        regionId: String(item.regionId || "").trim() || null,
        order: toFiniteNumber(item.order, Number.MAX_SAFE_INTEGER),
        createdAt: toFiniteNumber(item.createdAt, 0),
        updatedAt: toFiniteNumber(item.updatedAt, 0),
      };
    })
    .filter(Boolean);

  const regions = Object.entries(regionsRaw)
    .map(([id, value]) => {
      const item = value && typeof value === "object" ? value : {};
      const name = String(item.name || "").trim();
      if (!id || !name) return null;
      return { id, name, order: toFiniteNumber(item.order, Number.MAX_SAFE_INTEGER), createdAt: toFiniteNumber(item.createdAt, 0), updatedAt: toFiniteNumber(item.updatedAt, 0) };
    })
    .filter(Boolean)
    .sort(sortByOrderThenName);

  return { categories, regions, items };
}

export function groupPublicTrailsByCatalog(apiItems, catalogRaw) {
  const itemsList = Array.isArray(apiItems) ? apiItems : [];
  const catalog = normalizePublicTrailsCatalog(catalogRaw || {});
  const categories = catalog.categories;
  const regions = catalog.regions;
  const regionNameById = Object.fromEntries(regions.map((region) => [region.id, region.name]));
  const itemMetaByFilename = {};
  for (const meta of catalog.items) {
    itemMetaByFilename[meta.filename] = meta;
  }

  const enrichedItems = itemsList.map((item) => {
    const filename = String(item?.filename || "").trim();
    const meta = itemMetaByFilename[filename] || null;
    return {
      ...(item || {}),
      categoryId: meta?.categoryId || null,
      regionId: meta?.regionId || null,
      region: regionNameById[meta?.regionId] || "",
      order: toFiniteNumber(meta?.order, Number.MAX_SAFE_INTEGER),
    };
  });

  const grouped = categories.map((cat) => {
    const catItems = enrichedItems
      .filter((it) => String(it?.categoryId || "") === cat.id)
      .sort(sortTrailItems);
    return {
      ...cat,
      items: catItems,
    };
  });

  const uncategorized = enrichedItems
    .filter((it) => {
      const cid = String(it?.categoryId || "").trim();
      if (!cid) return true;
      return !categories.some((cat) => cat.id === cid);
    })
    .sort(sortTrailItems);

  return { categories, regions, grouped, uncategorized, itemMetaByFilename };
}
